// ═══════════════════════════════════════════════════════════════
// HotWash — Bulk Import Wizard
// 3-step CSV import: Upload → Preview/Validate → Results
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import {
  Title, Text, Paper, Button, Group, Stack, Badge, Progress, Table, Stepper,
} from '../../components/gab/GabComponents';
import { IconUpload, IconCheck, IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import type { ObservationTier, ObservationCategory, ObservationPhase } from '../../types';

interface ParsedRow {
  incident_title: string;
  tier: string;
  observation_title: string;
  description: string;
  category: string;
  phase: string;
  suggested_remedy: string;
}

interface ValidatedRow {
  row: ParsedRow;
  index: number;
  valid: boolean;
  error: string;
  incidentId: string;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current.trim());
  return result;
}

function parseCSV(text: string): ParsedRow[] {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map(line => {
    const vals = parseCSVLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj as unknown as ParsedRow;
  });
}

export default function BulkImport() {
  const navigate = useNavigate();
  const { submitObservation, loadObservations } = useHotWashStore();
  const { incidents } = useAuthStore();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [rows, setRows] = useState<ValidatedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ success: number; failed: number; errors: Array<{ row: number; msg: string }> } | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCSV(text);

      // Validate rows
      const validated: ValidatedRow[] = parsed.map((row, i) => {
        const errors: string[] = [];
        let incidentId = '';

        if (!row.incident_title) errors.push('Missing incident title');
        else {
          const inc = incidents.find(inc => inc.name.toLowerCase() === row.incident_title.toLowerCase());
          if (!inc) errors.push(`Incident "${row.incident_title}" not found`);
          else incidentId = String(inc.id);
        }

        if (!row.tier) errors.push('Missing tier');
        else if (!TIER_KEYS.includes(row.tier.toLowerCase() as ObservationTier)) errors.push(`Invalid tier: ${row.tier}`);

        if (!row.observation_title) errors.push('Missing title');
        if (!row.description) errors.push('Missing description');

        if (row.category && !CATEGORIES.includes(row.category.toLowerCase() as ObservationCategory)) {
          errors.push(`Invalid category: ${row.category}`);
        }
        if (row.phase && !PHASES.includes(row.phase.toLowerCase() as ObservationPhase)) {
          errors.push(`Invalid phase: ${row.phase}`);
        }

        return { row, index: i + 2, valid: errors.length === 0, error: errors.join('; '), incidentId };
      });

      setRows(validated);
      setStep(1);
    };
    reader.readAsText(file);
  };

  const validRows = rows.filter(r => r.valid);
  const errorRows = rows.filter(r => !r.valid);

  const runImport = async () => {
    setImporting(true);
    setProgress(0);
    const errors: Array<{ row: number; msg: string }> = [];
    let success = 0;

    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i];
      try {
        await submitObservation({
          incidentId: r.incidentId,
          tier: r.row.tier.toLowerCase() as ObservationTier,
          title: r.row.observation_title,
          description: r.row.description,
          category: (r.row.category?.toLowerCase() || 'other') as ObservationCategory,
          phase: (r.row.phase?.toLowerCase() || 'response') as ObservationPhase,
          suggestedRemedy: r.row.suggested_remedy || '',
          isAnonymous: false,
        });
        success++;
      } catch (err) {
        errors.push({ row: r.index, msg: err instanceof Error ? err.message : 'Failed' });
      }
      setProgress(Math.round(((i + 1) / validRows.length) * 100));
    }

    await loadObservations();
    setResults({ success, failed: errors.length, errors });
    setStep(2);
    setImporting(false);
  };

  const downloadTemplate = () => {
    const csv = 'incident_title,tier,observation_title,description,category,phase,suggested_remedy\nSpring Storm Response 2026,observation,Radio comms delayed,"10-minute delay on channel 3",communication,response,Switch to backup frequency';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'hotwash_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Title order={3} mb="md" c="black">Bulk Import</Title>

      <Stepper active={step} mb="lg" size="sm">
        <Stepper.Step label="Upload" description="CSV file" />
        <Stepper.Step label="Preview" description="Validate data" />
        <Stepper.Step label="Results" description="Import complete" />
      </Stepper>

      {/* Step 0: Upload */}
      {step === 0 && (
        <Stack gap="md">
          <Paper
            p="xl" radius="sm" withBorder ta="center"
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => fileRef.current?.click()}
          >
            <IconUpload size={32} color="var(--blurple)" style={{ marginBottom: 8 }} />
            <Text fw={500}>Click to upload CSV</Text>
            <Text size="xs" c="dimmed">or drag and drop</Text>
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={handleFile} />
          </Paper>

          <Button variant="subtle" size="xs" leftSection={<IconDownload size={14} />} onClick={downloadTemplate}>
            Download Template
          </Button>

          <Paper p="sm" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="xs">CSV Columns</Text>
            <Table withTableBorder withColumnBorders fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Column</Table.Th>
                  <Table.Th>Required</Table.Th>
                  <Table.Th>Valid Values</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr><Table.Td>incident_title</Table.Td><Table.Td>Yes</Table.Td><Table.Td>Must match existing incident name</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>tier</Table.Td><Table.Td>Yes</Table.Td><Table.Td>observation, improvement_needed, problem_identified, critical_error</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>observation_title</Table.Td><Table.Td>Yes</Table.Td><Table.Td>Free text</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>description</Table.Td><Table.Td>Yes</Table.Td><Table.Td>Free text</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>category</Table.Td><Table.Td>No</Table.Td><Table.Td>communication, logistics, personnel, equipment, procedures, medical, other</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>phase</Table.Td><Table.Td>No</Table.Td><Table.Td>preparation, response, recovery, mitigation</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>suggested_remedy</Table.Td><Table.Td>No</Table.Td><Table.Td>Free text</Table.Td></Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Step 1: Preview */}
      {step === 1 && !importing && (
        <Stack gap="md">
          {errorRows.length > 0 && (
            <Paper p="sm" radius="sm" bg="var(--error-light)" style={{ border: '1px solid var(--error)' }}>
              <Text size="sm" c="var(--error)" fw={500}>
                {errorRows.length} row(s) with errors — will be skipped
              </Text>
            </Paper>
          )}
          <Text size="sm">{validRows.length} valid row(s) ready to import</Text>

          <Paper radius="sm" withBorder style={{ overflow: 'auto', maxHeight: 400 }}>
            <Table withTableBorder fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Incident</Table.Th>
                  <Table.Th>Tier</Table.Th>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(r => (
                  <Table.Tr key={r.index} bg={r.valid ? undefined : 'var(--error-light)'}>
                    <Table.Td>{r.index}</Table.Td>
                    <Table.Td>{r.row.incident_title}</Table.Td>
                    <Table.Td>{r.row.tier}</Table.Td>
                    <Table.Td>{r.row.observation_title}</Table.Td>
                    <Table.Td style={{ maxWidth: 200 }}>{r.row.description.slice(0, 60)}...</Table.Td>
                    <Table.Td>
                      {r.valid ? (
                        <Badge size="xs" color="var(--color-success)">Valid</Badge>
                      ) : (
                        <Text size="xs" c="var(--color-error)">{r.error}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>

          <Group>
            <Button variant="subtle" onClick={() => { setStep(0); setRows([]); }}>Back</Button>
            <Button onClick={runImport} disabled={validRows.length === 0}>
              Import {validRows.length} Observation{validRows.length !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      )}

      {/* Importing progress */}
      {importing && (
        <Paper p="xl" radius="sm" withBorder ta="center">
          <Text fw={500} mb="sm">Importing... {Math.round(progress * validRows.length / 100)} of {validRows.length}</Text>
          <Progress value={progress} size="lg" radius="xl" mb="sm" />
        </Paper>
      )}

      {/* Step 2: Results */}
      {step === 2 && results && (
        <Stack gap="md">
          <Paper p="lg" radius="sm" withBorder ta="center">
            {results.failed === 0 ? (
              <IconCheck size={48} color="var(--success)" style={{ marginBottom: 8 }} />
            ) : (
              <IconAlertTriangle size={48} color="var(--warning)" style={{ marginBottom: 8 }} />
            )}
            <Text fw={600} size="lg">
              {results.success} observation{results.success !== 1 ? 's' : ''} imported
            </Text>
            {results.failed > 0 && (
              <Text size="sm" c="var(--color-error)">{results.failed} failed</Text>
            )}
          </Paper>

          {results.errors.length > 0 && (
            <Paper p="sm" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Failed Rows</Text>
              {results.errors.map((err, i) => (
                <Text key={i} size="xs" c="var(--color-error)">Row {err.row}: {err.msg}</Text>
              ))}
            </Paper>
          )}

          <Group>
            <Button variant="subtle" onClick={() => { setStep(0); setRows([]); setResults(null); }}>
              Import More
            </Button>
            <Button onClick={() => navigate('/hotwash/observations')}>View Observations</Button>
          </Group>
        </Stack>
      )}
    </div>
  );
}
